import { RESOURCE_LABELS } from '@bienenkampf/shared';
import { Badge, Card, Progress } from '@bienenkampf/ui';
import { formatAbsRel, formatDuration, formatNumber } from '../lib/time.js';
import type { HiveView } from './types.js';

/** Winterkarte: Zehrung pro Stunde, Reichweite des Vorrats, Hungerstatus, Erholung. */
export function WinterCard({ view, now }: { view: HiveView; now: Date }) {
  const w = view.winter;
  const hours = w.hoursOfStores;
  const critical = w.isWinter && hours !== null && hours < 24;
  const starving = w.isWinter && !!w.starvingSince;
  const storeText = w.consumeOrder.map((r) => RESOURCE_LABELS[r]).join(', dann ');
  const seasonHoursLeft = (view.season.endsAt.getTime() - now.getTime()) / 3_600_000;
  const coverage =
    w.isWinter && hours !== null && seasonHoursLeft > 0
      ? Math.min(1, hours / seasonHoursLeft)
      : null;

  return (
    <Card
      title="Winter"
      action={
        starving ? (
          <Badge tone="red">Hunger</Badge>
        ) : w.isWinter ? (
          <Badge tone={critical ? 'red' : 'sky'}>Winter</Badge>
        ) : (
          <Badge>Tracht</Badge>
        )
      }
    >
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <dt className="text-stone-400">Zehrung</dt>
        <dd className="tabular-nums">
          {formatNumber(Math.round(w.consumptionPerHour * 10) / 10)} / h ({storeText})
        </dd>
        <dt className="text-stone-400">Vorrat reicht</dt>
        <dd className="tabular-nums">
          {hours === null ? '–' : hours > 24 * 60 ? '> 60 Tage' : formatDuration(hours * 3600)}
        </dd>
        <dt className="text-stone-400">{w.isWinter ? 'Winter endet' : 'Nächster Winter'}</dt>
        <dd>
          {w.isWinter
            ? formatAbsRel(view.season.endsAt, now)
            : w.nextWinterAt
              ? formatAbsRel(w.nextWinterAt, now)
              : '–'}
        </dd>
        <dt className="text-stone-400">Untergrenze</dt>
        <dd className="tabular-nums">{formatNumber(w.minPopulation)} Bienen</dd>
      </dl>
      {coverage !== null && (
        <div className="mt-2">
          <Progress value={coverage} tone={coverage >= 1 ? 'sky' : 'amber'} />
          <p className="mt-1 text-xs text-stone-500">
            {coverage >= 1
              ? 'Der Vorrat reicht bis zum Winterende.'
              : `Der Vorrat deckt ${Math.round(coverage * 100)} % des restlichen Winters.`}
          </p>
        </div>
      )}
      {starving && (
        <p className="mt-2 rounded-md border border-red-900/60 bg-red-950/40 p-2 text-xs text-red-200">
          Vorrat leer seit {formatAbsRel(w.starvingSince!, now)}: pro Stunde verhungern{' '}
          {Math.round(w.decayPct * 100)} % der Bienen (nie unter {formatNumber(w.minPopulation)}).
          Nach {w.decayBuildingAfterHours} h Hunger verfällt die höchste Wabe um eine Stufe. Nektar
          oder Pollen einlagern stoppt das sofort.
        </p>
      )}
      {!w.isWinter && (
        <p className="mt-2 text-xs text-stone-500">
          Im Winter versiegt die Produktion, Bienen sind langsam und schwach, die Brut ruht. Wer
          genug Nektar und Pollen eingelagert hat, verliert nichts.
          {w.winterLost > 0 &&
            ` Beim letzten Winterende kehren ${Math.round(w.recoveryFraction * 100)} % der verhungerten Bienen zurück.`}
        </p>
      )}
    </Card>
  );
}
